import { Inject, Injectable } from '@angular/core';
import { storageKey } from '../../app.const';
import { FlowType } from '../model/component/steps/flow-type.model';
import { StateService } from '../state/state.service';
import { OrderStateService } from '../state/order.state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ENVIRONMENT_TOKEN } from '../injection.tokens';
import { INavigationStep } from 'libs/shared/src/lib/component/steps/navigation-steps.component';
import { ActivatedRoute } from '@angular/router';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { FlowOptions } from '../model/component/steps/flow-options.model';
import { Flow } from '../model/component/steps/flow.model';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class FlowService {
  private currentStepIndex: number = 0;
  private steps: INavigationStep[];

  private _flowType: FlowType = FlowType.Standard;

  private flowTypeSubject: ReplaySubject<FlowType> = new ReplaySubject<FlowType>();
  public flowType$: Observable<FlowType> = this.flowTypeSubject.asObservable();

  public set FlowType(value: FlowType) {
    this._flowType = value;
    this.steps = null;

    const model: Flow = { value, options: this.getFlowOptions(value) };
    this.stateService.setItem(storageKey.flowType, JSON.stringify(model));

    this.flowOptions = model.options;
    this.flowTypeSubject.next(value);
  }

  public get FlowType(): FlowType {
    return this._flowType;
  }

  public flowOptions: FlowOptions;

  public constructor(@Inject(ENVIRONMENT_TOKEN) protected environment: any, private stateService: StateService, orderStateService: OrderStateService) {
    const model: Flow = JSON.parse(this.stateService.getItem(storageKey.flowType)) as Flow;
    if (model) {
      this._flowType = model.value;
      this.flowOptions = model.options;
    }

    orderStateService.state$.pipe(untilDestroyed(this)).subscribe((order) => {
      if (order?.isOnlyGiftCardOrder()) {
        this._flowType = FlowType.GiftCard;
        this.flowOptions = null;
      }
    });
  }

  getSteps() {
    const navigationSteps: any[] = Object.values(this.environment.navigationSteps[this._flowType]);
    const steps: INavigationStep[] = [];

    navigationSteps.forEach((step, index) => {
      steps.push({
        key: step,
        canBack: (index + 1 === this.getCurrentStepIndex() && !this.isLastStep()) ?? false,
        index: index
      });
    });

    this.steps = steps;

    return this.steps;
  }

  getCurrentStepIndex(): number {
    return this.currentStepIndex;
  }

  getStep(pageIdentity: string) {
    const steps = this.getSteps();
    return steps.find((step) => step.key === pageIdentity);
  }

  setCurrentStepIndex(index: number): void {
    this.currentStepIndex = index;
  }

  public initCurrentStep(route: ActivatedRoute) {
    while (route.firstChild) {
      route = route.firstChild;
    }
    const pageIdentity = route.snapshot.data['pageIdentify'] || undefined;
    const step = this.getStep(pageIdentity);
    this.setCurrentStepIndex(step.index);
  }

  isLastStep() {
    return this.getCurrentStepIndex() === (this.steps?.length ?? 0) - 1;
  }

  private getFlowOptions(value: FlowType) {
    if (value === FlowType.Voucher) {
      return { activeTheme: this.environment.voucherShop.activeTheme };
    }
  }
}
